import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TegelModule } from '@scania/tegel-angular-17';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { TableHeader } from '../../models/table-header';
import { UtcToLocalPipe } from '../../pipes/utc-to-local.pipe';

@Component({
  selector: 'app-table',
  imports: [
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    TegelModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatIconModule,
    UtcToLocalPipe,
  ],
  providers: [DatePipe],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent<T> implements OnInit, AfterViewInit, OnChanges {
  @Input() headers: TableHeader[] = [];
  @Input() content: T[] = [];

  @Input() hasSelector = false;
  @Input() hasVisualize = false;
  @Input() hasEdit = false;
  @Input() hasDelete = false;
  @Input() hasRemove = false;
  @Input() showFilter = false;
  @Input() hasPaginator = true;

  @Output() selectedItens: EventEmitter<T[]> = new EventEmitter<T[]>();
  @Output() visualizing: EventEmitter<T> = new EventEmitter<T>();
  @Output() editing: EventEmitter<T> = new EventEmitter<T>();
  @Output() deleting: EventEmitter<T> = new EventEmitter<T>();
  @Output() removing: EventEmitter<T> = new EventEmitter<T>();

  @ViewChild(MatPaginator) paginator = new MatPaginator();
  @ViewChild(MatSort) sort = new MatSort();

  displayedColumns: string[] = [];
  dataSource: MatTableDataSource<T> = new MatTableDataSource<T>();
  selection = new SelectionModel<T>(true, []);

  searchValue = '';

  ngOnInit(): void {
    this.displayedColumns = this.headers
      .filter((h) => !h.hidden)
      .map((h) => h.value);

    if (this.hasSelector) {
      this.displayedColumns.splice(0, 0, 'select');
    }

    if (this.hasVisualize || this.hasEdit || this.hasDelete || this.hasRemove) {
      this.displayedColumns.push('actions');
    }

    this.dataSource = new MatTableDataSource(this.content);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    this.dataSource.filterPredicate = (data: any, filter: string): boolean => {
      const dataStr = this.flattenValues(data).join(' ').toLowerCase();
      return dataStr.includes(filter.trim().toLowerCase());
    };
  }

  flattenValues(obj: any): string[] {
    const values: string[] = [];

    for (const value of Object.values(obj)) {
      if (value === null || value === undefined) continue;

      if (typeof value === 'object') {
        values.push(...this.flattenValues(value));
      } else {
        values.push(String(value));
      }
    }

    return values;
  }

  ngOnChanges() {
    if (this.content) {
      this.dataSource.data = [...this.content];
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    }
  }

  applyFilter() {
    this.dataSource.filter = this.searchValue.trim().toLocaleLowerCase();
  }

  clearFilter() {
    this.searchValue = '';
    this.dataSource.filter = this.searchValue.trim().toLocaleLowerCase();
  }

  visualize(item: T) {
    this.visualizing.emit(item);
  }

  edit(item: any) {
    this.editing.emit(item);
  }

  exclude(item: any) {
    this.deleting.emit(item);
  }

  remove(item: any) {
    this.removing.emit(item);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.selectedItens.emit(this.selection.selected);
      return;
    }

    this.selection.select(...this.dataSource.data);
    this.selectedItens.emit(this.selection.selected);
  }

  toggleRow(row: any) {
    this.selection.toggle(row);
    this.selectedItens.emit(this.selection.selected);
  }

  clearAllSelection() {
    this.selection.clear();
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      row.position + 1
    }`;
  }

  getNestedValue(object: any, key: string): any {
    return key.split('.').reduce((o, i) => (o ? o[i] : null), object);
  }
}
